import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SearchIcon } from '../../../../assets/icons/search-icon.svg';
import { useAppSelector } from '../../../../redux/hooks';
import CustomDatePicker from '../../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ChargingSessionSidePanel from '../../../../shared-components/table-side-panel/ChargingSessionSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import emspSessionApi from '../../../../utils/api/v2/emsp-session';

interface SessionData {
  id: string;
  _id: string;
  chargingPointSn: string;
  chargingStation: string; // Station ID reference
  transactionStartsAt: string; // ISO date string
  transactionEndsAt: string; // ISO date string
  powerConsumedInKwh: string; // API returns string
  totalAmount: string; // API returns string
  chargingStationDetails: {
    _id: string;
    name: string;
  };
}

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}
interface VehicleProperties {
  vehicle: string;
  type: string;
}

/**
 * SessionDetails component to show charging session history for an end consumer.
 * @param {VehicleProperties} param0 - Component properties
 * @param {string} param0.vehicle - The vehicle prop
 * @param {string} param0.type - The type prop
 * @returns {JSX.Element} JSX.Element
 */
function SessionDetails({ vehicle, type }: VehicleProperties) {
  const { t } = useTranslation('endConsumer');
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  const [filteredList, setFilteredList] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [sortData, setSortData] = useState<{ sortField: string | null; sortBy: 'asc' | 'desc' | null }>({
    sortField: null,
    sortBy: null,
  });

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalResults, setTotalResults] = useState<number>(0);
  const isMounted = useRef<boolean>(false);

  /**
   * Fetch session list from API
   * @param {string} userCustomId - The custom user ID
   * @param {string} [search] - Optional search query
   * @param {number} [pageParameter] - Optional page number
   * @param {number} [limitParameter] - Optional page limit
   * @param {string | null} [sortFieldParameter] - Optional sort field
   * @param {'asc' | 'desc' | null} [sortByParameter] - Optional sort direction
   * @returns {Promise<void>} Promise resolving when fetch is complete
   */
  const fetchSessions = useCallback(
    async (
      userCustomId: string,
      search?: string,
      pageParameter?: number,
      limitParameter?: number,
      sortFieldParameter?: string | null,
      sortByParameter?: 'asc' | 'desc' | null,
    ) => {
      setLoading(true);
      try {
        const query: any = {
          page: pageParameter || page,
          limit: limitParameter || limit,
        };

        if (dateRange.startDate) {
          query.startDate = dayjs(dateRange.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        if (dateRange.endDate) {
          query.endDate = dayjs(dateRange.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        // Add search parameter
        if (search && search.trim() !== '') {
          query.searchBy = search.trim();
        }

        const effectiveSortField =
          sortFieldParameter !== undefined ? sortFieldParameter : sortData.sortField;
        const effectiveSortBy =
          sortByParameter !== undefined ? sortByParameter : sortData.sortBy;

        if (effectiveSortField && effectiveSortBy) {
          query.sortField = effectiveSortField;
          query.sortBy = effectiveSortBy;
        }

        query.vehicleId = vehicle;
        query.userCustomId = userCustomId;
        const response =
          type === 'personal'
            ? await emspSessionApi.fetchPersonalSessionList(query)
            : await emspSessionApi.fetchCommercialSessionVehicalList(query);
        const resourcesData: any = response?.data;
        let data: any[] = [];
        if (Array.isArray(resourcesData?.results)) {
          data = resourcesData.results;
          setPage(resourcesData.page || 1);
          setLimit(resourcesData.limit || 20);
          setTotalPages(resourcesData.totalPages || 1);
          setTotalResults(resourcesData.totalResults ?? data.length);
        } else if (Array.isArray(resourcesData)) {
          data = resourcesData;
          setPage(1);
          setLimit(data.length || 20);
          setTotalPages(1);
          setTotalResults(data.length);
        } else if (Array.isArray(resourcesData?.data)) {
          data = resourcesData.data;
          setPage(resourcesData.page || 1);
          setLimit(resourcesData.limit || 20);
          setTotalPages(resourcesData.totalPages || 1);
          setTotalResults(resourcesData.totalResults ?? data.length);
        }

        // Apply client-side sorting as well to ensure instant, consistent sorting across all columns
        if (effectiveSortField && effectiveSortBy && data.length > 0) {
          data = [...data].sort((a: any, b: any) => {
            let valueA: any = '';
            let valueB: any = '';

            if (effectiveSortField === 'startTime') {
              valueA = new Date(
                a?.sessionStartAt || a?.transactionStartsAt || a?.createdAt || a?.startedAt || 0,
              ).getTime();
              valueB = new Date(
                b?.sessionStartAt || b?.transactionStartsAt || b?.createdAt || b?.startedAt || 0,
              ).getTime();
            } else if (effectiveSortField === 'powerConsumed') {
              valueA = Number(a?.powerConsumedInKwh ?? a?.energyKwh ?? a?.powerConsumed ?? 0);
              valueB = Number(b?.powerConsumedInKwh ?? b?.energyKwh ?? b?.powerConsumed ?? 0);
            } else if (effectiveSortField === 'amount') {
              valueA = Number(a?.totalCost?.excl_vat ?? a?.totalCost ?? a?.amount ?? 0);
              valueB = Number(b?.totalCost?.excl_vat ?? b?.totalCost ?? b?.amount ?? 0);
            } else if (effectiveSortField === 'locationId') {
              valueA = String(a?.locationId || '').toLowerCase();
              valueB = String(b?.locationId || '').toLowerCase();
            } else if (effectiveSortField === 'chargingPointSn') {
              valueA = String(
                a?.chargingPointSn || a?.evseUid || a?.userCustomId || a?.station?.deviceNo || '',
              ).toLowerCase();
              valueB = String(
                b?.chargingPointSn || b?.evseUid || b?.userCustomId || b?.station?.deviceNo || '',
              ).toLowerCase();
            } else {
              valueA = a?.[effectiveSortField] ?? '';
              valueB = b?.[effectiveSortField] ?? '';
            }

            if (valueA < valueB) return effectiveSortBy === 'asc' ? -1 : 1;
            if (valueA > valueB) return effectiveSortBy === 'asc' ? 1 : -1;
            return 0;
          });
        }

        setFilteredList(data);
      } catch (error) {
        console.error('Failed to fetch session list:', error);
      } finally {
        setLoading(false);
      }
    },
    [dateRange, vehicle, page, limit, type, sortData.sortField, sortData.sortBy],
  );

  /**
   * Handles updating the search term state from CustomInput
   * @param {string} name - Field name
   * @param {string | number} value - Input value (string or number)
   */
  const handleSearchChange = (name: string, value: string | number): void => {
    setSearchTerm(String(value));
  };

  /**
   * Handle date range change
   * @param {string} name - The field name (startDate or endDate)
   * @param {string} value - The date string value
   */
  const handleDateChange = (name: string, value: string) => {
    setDateRange((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle sort change event and fetch sorted data
   * @param {any} newSortData - The updated sort data containing sortField and sortBy
   */
  const handleSortChange = (newSortData: any) => {
    setSortData(newSortData);
    setPage(1);
    if (selectedEndConsumer?.userCustomId) {
      fetchSessions(
        selectedEndConsumer.userCustomId,
        searchTerm,
        1,
        limit,
        newSortData?.sortField,
        newSortData?.sortBy,
      );
    }
  };

  /**
   * Handle pagination limit and page change
   * @param {any} value - Page and limit values
   */
  const pageLimitChange = (value: any) => {
    const newPage = value?.page || 1;
    const newLimit = value?.limit || 20;
    setPage(newPage);
    setLimit(newLimit);
    if (selectedEndConsumer?.userCustomId) {
      fetchSessions(
        selectedEndConsumer.userCustomId,
        searchTerm,
        newPage,
        newLimit,
        sortData.sortField,
        sortData.sortBy,
      );
    }
  };

  /**
   * Handle open charge session details
   * @param {any} rowData - The session data
   */
  const handleOpenChargeSessionDetails = (rowData: any) => {
    const sessionId = rowData?.sessionId || rowData?._id || rowData?.id;
    if (!sessionId) return;
    setIsOpen(true);
    setModelDetails({
      title: t('chargingSessionDetails'),
      content: <ChargingSessionSidePanel fetchIdFrom="props" id={sessionId} />,
    });
  };

  const columns: any[] = [
    {
      label: t('dateTime'),
      accessorKey: 'startTime',
      sortAllow: true,
      /**
       * SessionData
       * @param {any} currentRow - Current row data
       * @returns {JSX.Element} - Custom data
       */
      customData: (currentRow: any) => {
        const startIso =
          currentRow?.sessionStartAt ||
          currentRow?.transactionStartsAt ||
          currentRow?.createdAt ||
          currentRow?.startedAt;
        return (
          <div>
            <p>{startIso ? ConvertUTCtoDate(startIso) : '-'}</p>
            <p>{startIso ? convertUtcTo12HourTime(startIso) : ''}</p>
          </div>
        );
      },
    },
    {
      label: t('locationId'),
      accessorKey: 'locationId',
      sortAllow: true,
      /**
       * Rendering station name with hyper link
       * @param {any} currentRow - current row data
       * @returns {JSX.Element} - custom data
       */
      customData: (currentRow: any) => <p>{currentRow?.locationId || 'N/A'}</p>,
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn',
      sortAllow: true,
      /**
       * Rendering device name with hyper link
       * @param {any} currentRow - current row data
       * @returns {JSX.Element} - custom data
       */
      customData: (currentRow: any) => (
        <p>
          {currentRow?.chargingPointSn ||
            currentRow?.evseUid ||
            currentRow?.userCustomId ||
            currentRow?.station?.deviceNo ||
            '-'}
        </p>
      ),
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed',
      sortAllow: true,
      /**
       * KWh Consumed
       * @param {any} currentRow - The currentRow
       * @returns {JSX.Element} - Custom power consumed view
       */
      customData: (currentRow: any) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenChargeSessionDetails(currentRow)}>
          {ToFixedValue(
            Number(
              currentRow?.powerConsumedInKwh ??
                currentRow?.energyKwh ??
                currentRow?.powerConsumed ??
                0,
            ),
            3,
          )}{' '}
          kWh
        </p>
      ),
    },
    {
      label: t('amount'),
      accessorKey: 'amount',
      sortAllow: true,
      /**
       * SessionData
       * @param {any} row - Row data
       * @returns {JSX.Element} - Amount display
       */
      customData: (row: any) => (
        <p className="amount-text">
          {ToFixedValue(row?.totalCost?.excl_vat ?? row?.totalCost ?? 0, 2)} {row.currency}
        </p>
      ),
    },
  ];

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    if (!isMounted.current) {
      isMounted.current = true;
      fetchSessions(selectedEndConsumer.userCustomId, searchTerm, 1, limit);
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchSessions(selectedEndConsumer.userCustomId, searchTerm, 1, limit);
    }, 500); // wait 500ms after typing stops

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(delayDebounce);
  }, [searchTerm, dateRange, selectedEndConsumer, fetchSessions, limit]);

  return (
    <div className="session-details-wrap-v2">
      <div className="session-details-filters">
        <div className="search-filter">
          <CustomInput
            type="text"
            name="search"
            placeholder={t('searchHere')}
            value={searchTerm}
            onChangeValue={handleSearchChange}
            svgIcon={<SearchIcon />}
          />
        </div>
        <div className="date-filters">
          <CustomDatePicker
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleDateChange}
            placeholder="DD/MM/YYYY"
          />
          <CustomDatePicker
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleDateChange}
            placeholder="DD/MM/YYYY"
          />
        </div>
      </div>

      <div className="session-details-table-wrap">
        <SidePaneModel
          open={isOpen}
          title={modelDetails.title}
          content={modelDetails.content}
          closePanel={handleCloseModal}
          url={modelDetails.url}
          preventQueryClear
        />
        <CustomTable
          header={columns}
          data={filteredList}
          loading={loading}
          serialNo
          version="v1"
          height={400}
          sortData={sortData}
          handleSortChange={handleSortChange}
          disableHeader
          page={page}
          limit={limit}
          totalPages={totalPages}
          totalResults={totalResults}
          pageLimitChange={pageLimitChange}
        />
      </div>
    </div>
  );
}

export default SessionDetails;
